import React, { useState } from "react";
import { Button } from "../../components/template";

const quizData = [
  { question: "How would you describe your body build?", options: ["Lean", "Medium", "Large"] },
  { question: "How does your skin usually feel?", options: ["Dry, rough, chapped", "Sensitive, oily, gets sunburnt easily", "Thick, moist, and soft"] },
  { question: "How’s your natural complexion?", options: ["Dark, brownish, dry, or dull", "Fair, pinkish or golden", "Light, whitish, clear"] },
  { question: "How is your hair?", options: ["Dry, brown/black, knotted, brittle", "Fine, Straight, Red, Prone to balding, greying", "Thick, Oily, Wavy, Curly"] },
  { question: "Which of these is true for your body weight?", options: ["Low, tough to gain weight", "Medium, easy to gain & lose weight", "Overweight, tough to lose weight"] },
  { question: "How does your body temperature usually feel?", options: ["Cold, cold extremities", "High, feels warm, hot flashes, sweats", "Steady body temperature"] },
  { question: "How are your eyes?", options: ["Black/Brown", "Grey, Yellowish", "Blue, Liquid-y"] },
  { question: "How would you describe your way of speaking?", options: ["Speaks fast & unstructured", "Precise, clear to the point, sharp", "Slow, gentle"] },
  { question: "How’s your hunger generally?", options: ["Irregular, frequently skips meals", "Strong, Timely, Unbearable", "Good but manages to skip meals sometimes"] },
  { question: "How often do you feel thirsty?", options: ["Varies from day to day", "Often", "Rarely"] },
  { question: "How is your bowel movement usually?", options: ["Towards Constipation", "Loose - Thick", "Oily, Sluggish"] },
  { question: "How would you describe your weather tolerance?", options: ["Feel cold easily", "Feel hot easily", "Dislike dampness, prefer warm weather"] },
  { question: "How’s your energy level throughout the day?", options: ["Irregular, comes & goes in spurts", "Medium to high, can push more", "Good and long-lasting, good stamina"] },
  { question: "How is your memory usually?", options: ["Good short-term memory, but forgets easily", "Quick memory, accurate past and future", "Follows a plan, dislikes change"] }
];

const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-light px-6 py-10 flex flex-col items-center text-center">
    <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
      <h1 className="text-2xl md:text-3xl font-bold text-primary mb-4 md:mb-6">
        Prakriti Quiz
      </h1>

      <p className="text-gray-700 text-base md:text-lg mb-3">
        Knowing your Prakriti (Mind-Body Type) helps you choose the right diet,
        exercise, and lifestyle for lasting benefits.
      </p>

      <p className="text-[#578d48] text-base md:text-lg mb-4 font-semibold">
        Answer these questions honestly to discover your Prakriti.
        Select the option that describes you best.
      </p>

      <p className="text-gray-600 text-base md:text-lg mb-6">
        Note: If you are older than 40 years, answer based on yourself at age
        20–35 years.
      </p>

      <Button
        variant="primary"
        size="md"
        text="Start Quiz"
        onClick={onStart}
      />
    </div>
  </div>
);


const QuizPage = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleAnswerClick = (option) => {
    const updatedAnswers = [...answers];
    updatedAnswers[currentQuestion] = option;
    setAnswers(updatedAnswers);
    setError("");
  };

  const handleNext = () => {
    if (answers[currentQuestion] === null) {
      setError("Please select an option to proceed.");
      return;
    }
    setError("");
    if (currentQuestion < quizData.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const progressPercentage = ((currentQuestion + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6">
      <div className="w-full max-w-2xl bg-white p-6 rounded-lg shadow-md text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {currentQuestion + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div className="bg-primary h-2 rounded-full" style={{ width: `${progressPercentage}%` }}></div>
          </div>
        </div>
        <h2 className="text-xl font-semibold text-secondary mb-4">{quizData[currentQuestion].question}</h2>
        <div className="grid grid-cols-1 gap-4">
          {quizData[currentQuestion].options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerClick(option)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                answers[currentQuestion] === option ? "bg-primary text-white" : "bg-gray-200 text-gray-700"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>
      <div className="flex mt-6 space-x-4">
        {currentQuestion > 0 && <Button onClick={handlePrev} text="Previous" variant="whitish" />}
        <Button
          onClick={handleNext}
          text={currentQuestion === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
        />
      </div>
    </div>
  );
};

const ResultPage = ({ results, onRetake }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
    <div className="w-full max-w-lg bg-white p-6 rounded-lg text-gray-700 shadow-lg">
      <h2 className="text-xl md:text-3xl font-bold text-secondary mb-4">Your Dosha Analysis</h2>
      <p className="text-md md:text-lg">
        <span className="font-semibold text-primary">Primary Dosha:</span> {results.primary}
      </p>
      {results.secondary && (
        <p className="text-md md:text-lg">
          <span className="font-semibold text-primary">Secondary Dosha:</span> {results.secondary}
        </p>
      )}
      <p className="text-sm md:text-lg mt-4 max-w-2xl text-gray-500 mx-auto">
        Download this FREE guide to understand your traits and discover the best diet, lifestyle, and practices for balance.
      </p>
      <a
        href="/Prakriti Management Guide_Param Life.pdf"
        className="text-secondary font-bold text-sm underline block mt-3"
      >
        Prakriti Management Guide
      </a>
      <div className="flex justify-center mt-5">
        <Button onClick={onRetake} text="Retake Quiz" />
      </div>
    </div>
  </div>
);

const PrakritiQuiz = () => {
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [results, setResults] = useState(null);

  const startQuiz = () => setQuizStarted(true);

  const calculateResults = (answers) => {
    let vata = 0, pitta = 0, kapha = 0;

    answers.forEach((answer, index) => {
      const optionIndex = quizData[index].options.indexOf(answer);
      if (optionIndex === 0) vata++;
      if (optionIndex === 1) pitta++;
      if (optionIndex === 2) kapha++;
    });

    const scores = [
      { dosha: "Vata", count: vata },
      { dosha: "Pitta", count: pitta },
      { dosha: "Kapha", count: kapha }
    ].sort((a, b) => b.count - a.count);

    let primary = scores[0].dosha;
    let secondary = scores[1].count > 0 ? scores[1].dosha : null;

    // If all answers are the same dosha
    if (
      (vata === answers.length) ||
      (pitta === answers.length) ||
      (kapha === answers.length)
    ) {
      secondary = null;
    }

    // Handle ties
    if (scores[0].count === scores[1].count && secondary) {
      primary = `${scores[0].dosha} & ${scores[1].dosha}`;
      secondary = scores[2].count > 0 ? scores[2].dosha : null;
    }

    return { primary, secondary };
  };

  const completeQuiz = (answers) => {
    const results = calculateResults(answers);
    setResults(results);
    setQuizCompleted(true);
  };

  const retakeQuiz = () => {
    setQuizStarted(false);
    setQuizCompleted(false);
    setResults(null);
  };

  return (
    <div>
      {!quizStarted ? (
        <IntroPage onStart={startQuiz} />
      ) : quizCompleted ? (
        <ResultPage results={results} onRetake={retakeQuiz} />
      ) : (
        <QuizPage onComplete={completeQuiz} />
      )}
    </div>
  );
};

export default PrakritiQuiz;
